<template>
  <div class="order-planner">
    <b-alert
      v-if="!loading && plannerState.teamDefinitions.length === 0"
      class="alert-on-gray-bg mb-1 p-1"
      variant="info"
      dismissible
      :show="showNoTeamsAlert"
      @dismissed="showNoTeamsAlert = false"
    >
      {{ $t('order.planner.empty-team-definitions-help') }}
    </b-alert>

    <template v-else>
      <b-alert
        class="alert-on-gray-bg mb-1 p-1"
        variant="success"
        dismissible
        :show="showTestVersionAlert"
        @dismissed="dismissTestVersionAlert"
      >
        To jest testowa wersja funkcjonalności planera. Propozycje i problemy prosimy zgłaszać na adres biuro@eambulans.pl.
      </b-alert>

      <b-row
        v-if="plannerShowStats"
        :class="statsRowClasses"
      >
        <b-col
          v-for="card in plannerStatCards"
          :key="card.key"
          cols="12"
          md="6"
          xl="3"
          :class="['order-planner__stat-col', `order-planner__stat-col--${card.key}`]"
        >
          <rc-overlay
            :show="loading"
            rounded
          >
            <statistic-card-horizontal
              :icon="card.icon"
              :statistic="card.value"
              :statistic-title="card.title"
              :color="card.color"
            />
          </rc-overlay>
        </b-col>
      </b-row>

      <b-row class="order-planner__layout">
        <b-col
          cols="12"
          :md="showPlannerBacklogColumn ? 8 : 12"
          :xl="showPlannerBacklogColumn ? 9 : 12"
          class="order-2 order-md-1 order-planner__calendar-col"
        >
          <b-card
            no-body
            class="order-planner__calendar-card"
          >
            <div class="order-planner__calendar-content">
              <order-planner-calendar
                ref="calendar"
                :date="plannerState.date"
                :view-mode="plannerViewMode"
                :show-buffer="plannerShowBuffer"
                :sticky-headers="plannerStickyHeaders"
                :events="plannerState.events"
                :resources="plannerState.resources"
                @cell-double-clicked="handleCellDoubleClicked"
                @event-clicked="handleEventClicked"
                @event-created="handleEventCreated"
                @selected-date-change="handleSelectedDateChange"
                @event-updated="handleEventUpdated"
              />
            </div>
          </b-card>
        </b-col>

        <b-col
          v-if="showPlannerBacklogColumn"
          cols="12"
          md="4"
          xl="3"
          class="order-1 order-md-2 order-planner__backlog-column"
        >
          <div class="order-planner__backlog-sticky">
            <order-planner-backlog
              :key="backlogRenderKey"
              class="order-planner__backlog-card"
              :orders="plannerState.backlogItems"
            />
          </div>
        </b-col>
      </b-row>

      <order-planner-event-popup
        :anchor="selectedEventAnchor"
        :event="selectedEvent"
        :is-open="isEventPopupOpen"
        :team-definitions="plannerState.teamDefinitions"
        @change-assignment-offset="handleAssignmentOffsetChange"
        @change-duration="handleDurationChange"
        @close="closeEventPopup"
        @edit="goToOrderEdit(selectedEvent.orderId)"
        @remove-planning="removePlanning(selectedEvent.orderId)"
        @save-teams="handlePopupSaveTeams"
        @view="goToOrderView(selectedEvent.orderId)"
      />

      <order-planning-modal
        ref="planningModal"
        @planning-updated="handlePlanningModalUpdated"
        @hidden="handlePlanningModalHidden"
      />
    </template>
  </div>
</template>

<script>
import { defineComponent, ref, computed } from '@vue/composition-api'
import {
  BAlert,
  BCard,
  BCol,
  BRow,
} from 'bootstrap-vue'
import { useRouter } from '@core/utils/utils'
import StatisticCardHorizontal from '@core/components/statistics-cards/StatisticCardHorizontal.vue'
import RcOverlay from '@/layouts/components/rc/RcOverlay.vue'
import OrderPlannerBacklog from '@/views/orders/Planner/components/OrderPlannerBacklog.vue'
import OrderPlannerCalendar from '@/views/orders/Planner/components/OrderPlannerCalendar.vue'
import OrderPlannerEventPopup from '@/views/orders/Planner/components/OrderPlannerEventPopup.vue'
import OrderPlanningModal from '@/views/orders/OrderPlanning/OrderPlanningModal.vue'
import {
  IN_PROGRESS_ORDER_STATUSES,
  DONE_ORDER_STATUSES,
} from '@/views/orders/Planner/setup'
import { usePlannerConfig } from './composables/usePlannerConfig'
import { usePlannerNavigation } from './composables/usePlannerNavigation'
import { usePlannerData } from './composables/usePlannerData'
import { usePlannerEvents } from './composables/usePlannerEvents'
import { usePlannerRealtime } from './composables/usePlannerRealtime'

export default defineComponent({
  name: 'OrderPlanner',
  components: {
    BAlert,
    BCard,
    BCol,
    BRow,
    RcOverlay,
    StatisticCardHorizontal,
    OrderPlannerBacklog,
    OrderPlannerCalendar,
    OrderPlannerEventPopup,
    OrderPlanningModal,
  },
  setup() {
    const { route, router } = useRouter()

    const config = usePlannerConfig(route)
    const navigation = usePlannerNavigation(route, router)
    const data = usePlannerData(navigation.selectedDate, config)
    const planningModal = ref(null)
    const events = usePlannerEvents(data, {
      goToOrderView: navigation.goToOrderView,
      planningModal,
    })
    usePlannerRealtime(data.fetchPlannerData)

    const plannerOrdersForStats = computed(() => [
      ...data.plannerState.value.unplannedOrders,
      ...data.plannerState.value.plannedOrders,
    ])

    const countPlannerOrdersByStatuses = statuses => plannerOrdersForStats.value.filter(order => statuses.includes(order?.status)).length

    const plannerStats = computed(() => ({
      total: plannerOrdersForStats.value.length,
      inProgress: countPlannerOrdersByStatuses(IN_PROGRESS_ORDER_STATUSES),
      plannedNotAssigned: data.plannerState.value.events
        .filter(event => !event.original?.isAssigned).length,
      backlog: data.plannerState.value.backlogItems.length,
      done: countPlannerOrdersByStatuses(DONE_ORDER_STATUSES),
    }))

    const plannerStatCards = computed(() => [
      {
        key: 'total', title: 'Razem', value: plannerStats.value.total, icon: 'ListIcon', color: 'primary',
      },
      {
        key: 'inProgress', title: 'W trakcie', value: plannerStats.value.inProgress, icon: 'CrosshairIcon', color: 'primary',
      },
      {
        key: 'plannedNotAssigned', title: 'Nieprzypisane', value: plannerStats.value.plannedNotAssigned, icon: 'CalendarIcon', color: 'warning',
      },
      {
        key: 'done', title: 'Zrealizowane', value: plannerStats.value.done, icon: 'CheckIcon', color: 'success',
      },
    ].concat(config.showBacklog.value ? [] : [{
      key: 'backlog',
      title: 'Do zaplanowania',
      value: plannerStats.value.backlog,
      icon: 'InboxIcon',
      color: 'warning',
    }]))

    const statsRowClasses = computed(() => [
      'order-planner__stats',
      { 'order-planner__stats--five': plannerStatCards.value.length === 5 },
    ])

    const showPlannerBacklogColumn = computed(() => config.showBacklog.value && data.plannerState.value.backlogItems.length > 0)

    const showNoTeamsAlert = ref(true)

    const TEST_VERSION_ALERT_KEY = 'planner_test_version_alert_dismissed_at'
    const TEST_VERSION_ALERT_TTL_MS = 24 * 60 * 60 * 1000

    const isTestVersionAlertDismissed = () => {
      try {
        const raw = localStorage.getItem(TEST_VERSION_ALERT_KEY)
        if (!raw) return false
        return Date.now() - Number(raw) < TEST_VERSION_ALERT_TTL_MS
      } catch {
        return false
      }
    }

    const showTestVersionAlert = ref(!isTestVersionAlertDismissed())

    const dismissTestVersionAlert = () => {
      showTestVersionAlert.value = false
      try {
        localStorage.setItem(TEST_VERSION_ALERT_KEY, String(Date.now()))
      } catch {
        // ignore storage errors
      }
    }

    return {
      loading: data.loading,
      plannerState: data.plannerState,
      backlogRenderKey: data.backlogRenderKey,
      plannerViewMode: config.viewMode,
      plannerShowBuffer: config.showBuffer,
      plannerStickyHeaders: config.stickyHeaders,
      plannerShowStats: config.showStats,
      plannerShowBacklog: config.showBacklog,
      selectedDate: navigation.selectedDate,
      handleSelectedDateChange: navigation.handleSelectedDateChange,
      handleCellDoubleClicked: navigation.handleCellDoubleClicked,
      goToOrderView: navigation.goToOrderView,
      goToOrderEdit: navigation.goToOrderEdit,
      syncSelectedDateFromRoute: navigation.syncSelectedDateFromRoute,
      selectedEvent: events.selectedEvent,
      selectedEventAnchor: events.selectedEventAnchor,
      isEventPopupOpen: events.isEventPopupOpen,
      dragTimePendingModal: events.dragTimePendingModal,
      pendingPlannerDropOrder: events.pendingPlannerDropOrder,
      handleEventClicked: events.handleEventClicked,
      handleEventCreated: events.handleEventCreated,
      handleEventUpdated: events.handleEventUpdated,
      handleDurationChange: events.handleDurationChange,
      handleAssignmentOffsetChange: events.handleAssignmentOffsetChange,
      handlePopupSaveTeams: events.handlePopupSaveTeams,
      handlePlanningModalUpdated: events.handlePlanningModalUpdated,
      handlePlanningModalHidden: events.handlePlanningModalHidden,
      closeEventPopup: events.closeEventPopup,
      removePlanning: events.removePlanning,
      savePlanning: data.savePlanning,
      fetchPlannerData: data.fetchPlannerData,
      applyOptimisticPlannerEvent: data.applyOptimisticPlannerEvent,
      plannerStatCards,
      statsRowClasses,
      showPlannerBacklogColumn,
      showNoTeamsAlert,
      showTestVersionAlert,
      dismissTestVersionAlert,
      planningModal,
    }
  },
})
</script>

<style lang="scss" scoped>
.order-planner__stat-col--backlog {
  ::v-deep .truncate span {
    white-space: nowrap;
  }
}

@media (min-width: 1200px) {
  .order-planner__stats--five {
    .order-planner__stat-col {
      flex: 0 0 20%;
      max-width: 20%;
    }
  }
}

// Mobile: backlog scrolls internally; calendar keeps page-level vertical scroll.
@media (max-width: 767.98px) {
  .order-planner__calendar-card {
    max-height: none;
  }

  .order-planner__calendar-content {
    overflow: visible;
  }

  .order-planner__backlog-sticky {
    max-height: 33vh;
    display: flex;
    flex-direction: column;
  }

  .order-planner__backlog-card {
    flex: 1 1 auto;
    min-height: 0;
    height: auto !important;
  }
}

// Desktop: sticky backlog
@media (min-width: 768px) {
  .order-planner__layout {
    align-items: flex-start;
  }

  .order-planner__backlog-column {
    align-self: stretch;
  }

  .order-planner__backlog-sticky {
    position: sticky;
    top: 100px;
    max-height: calc(100vh - 100px - 15px);
    display: flex;
    flex-direction: column;
  }

  .order-planner__backlog-card {
    flex: 1 1 auto;
    min-height: 0;
    height: auto !important;
  }
}
</style>
